import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableHead, TableRow, Grid, FormHelperText } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import { Button, CollapseCard } from "@totalsoft/rocket-ui";
import { useTranslation } from "react-i18next";
import ShareholderRowComponent from "./ShareholderRowComponent";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import ShareholderSummary from "../shareholders/ShareholderSummary";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { DenseHeaderCell } from "components/common/TableStyles";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

export const ShareholderComponent = ({
  shareholderList,
  loadPartners,
  onPropertyChange,
  addShareholder,
  saveShareholders,
  deleteShareholder,
  shareholderCardExpanded,
  onShareholderCardExpanded,
  loadPartnerDetails,
  partnerId,
  saveDisabled,
  validationShareholderSelection,
  shareholdersSummary,
  shareholdersSummaryLoaded,
  hasWritePermission,
  openPartnerWindow,
  viewShareholderPhone
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);

  return (
    <CollapseCard
      id="shareholderCard"
      icon={PeopleIcon}
      iconColor="primary"
      title={t("Partner.Shareholders")}
      expanded={shareholderCardExpanded}
      onToggle={(_, expanded) => onShareholderCardExpanded(!expanded)}
      subheader={<ShareholderSummary shareholders={shareholdersSummary} summaryLoaded={shareholdersSummaryLoaded} />}
      defaultExpanded={synchronizationInProgress}
      toggleOnHeaderClick
    >
      <Grid container>
        <Grid item xs={12} sm={12} md={12}>
          <CardNoResultsWrapper content={shareholderList}>
            <Table>
              <TableHead>
                <TableRow>
                  <DenseHeaderCell>{t("Partner.UniqueID")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("Partner.Name")}</DenseHeaderCell>
                  {viewShareholderPhone && <DenseHeaderCell>{t("Partner.Phone")}</DenseHeaderCell>}
                  <DenseHeaderCell>{t("Partner.Percent")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("Partner.IsRealBeneficiary")}</DenseHeaderCell>
                  <DenseHeaderCell></DenseHeaderCell>
                  <DenseHeaderCell></DenseHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shareholderList?.map((document, idx) => {
                  return (
                    <ShareholderRowComponent
                      key={idx}
                      idx={idx}
                      shareholder={document}
                      loadPartners={loadPartners}
                      deleteShareholder={() => deleteShareholder(document)}
                      onPropertyChange={onPropertyChange(idx)}
                      hasWritePermission={hasWritePermission}
                      loadPartnerDetails={loadPartnerDetails}
                      validationShareholderSelection={validationShareholderSelection}
                      openPartnerWindow={openPartnerWindow}
                      viewShareholderPhone={viewShareholderPhone}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
          {!isValid(validationShareholderSelection) && shareholderList && shareholderList.length !== 0 && (
            <FormHelperText error={true} id="name-error-text">
              {getErrors(validationShareholderSelection)}
            </FormHelperText>
          )}
          <Button
            id="shareholder_addBtn"
            color="primary"
            variant="contained"
            onClick={() => addShareholder()}
            disabled={!hasWritePermission}
            sx={{ marginTop: "1.5rem" }}
          >
            {t("Add")}
          </Button>
          <Button
            id="shareholder_saveBtn"
            color="primary"
            variant="contained"
            onClick={() => saveShareholders(partnerId, shareholderList)}
            disabled={!hasWritePermission || saveDisabled || !isValid(validationShareholderSelection)}
            sx={{ marginLeft: ".25rem", marginTop: "1.5rem" }}
          >
            {t("Save")}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

ShareholderComponent.propTypes = {
  shareholderList: PropTypes.array,
  loadPartners: PropTypes.func.isRequired,
  deleteShareholder: PropTypes.func.isRequired,
  addShareholder: PropTypes.func.isRequired,
  saveShareholders: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  shareholderCardExpanded: PropTypes.bool,
  onShareholderCardExpanded: PropTypes.func,
  loadPartnerDetails: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  totalPercentageValid: PropTypes.bool,
  validationShareholderSelection: PropTypes.object,
  hasWritePermission: PropTypes.bool.isRequired,
  shareholdersSummary: PropTypes.array,
  shareholdersSummaryLoaded: PropTypes.bool,
  openPartnerWindow: PropTypes.func.isRequired,
  viewShareholderPhone: PropTypes.bool.isRequired
};

export default ShareholderComponent;
